import * as RadixPopover from "@radix-ui/react-popover";
import { Button, Checkbox, Icon, token } from "@may-i/mash-design-system";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { FloorPlan } from "~/entities/floor-plan/types";

export interface CameraFilterState {
  analyzed: boolean;
  notAnalyzed: boolean;
  lineTraffic: boolean;
  lineVisit: boolean;
  lineDefault: boolean;
  zoneDefault: boolean;
  zonePurchase: boolean;
  noEventSource: boolean;
}

export const DEFAULT_CAMERA_FILTER: CameraFilterState = {
  analyzed: true,
  notAnalyzed: true,
  lineTraffic: true,
  lineVisit: true,
  lineDefault: true,
  zoneDefault: true,
  zonePurchase: true,
  noEventSource: true,
};

export interface EventTypeAvailability {
  hasAnalyzed: boolean;
  hasNotAnalyzed: boolean;
  hasLineTraffic: boolean;
  hasLineVisit: boolean;
  hasLineDefault: boolean;
  hasZoneDefault: boolean;
  hasZonePurchase: boolean;
  hasNoEventSource: boolean;
}

interface CameraFilterPopoverProps {
  filter: CameraFilterState;
  availability: EventTypeAvailability;
  // (F17) 도면 필터 — 상단 독립 드롭다운에서 이 팝오버 안 '도면' 섹션(드롭다운)으로 통합
  floorPlans: FloorPlan[];
  selectedFloorPlanIds: Set<number>;
  /** 카메라가 있는 도면 id — 대상 없는 도면은 disabled */
  floorPlanIdsWithCameras: Set<number>;
  onApply: (filter: CameraFilterState, floorPlanIds: Set<number>) => void;
  /** 현재 필터에 매칭되는 카메라 수 — 트리거 카운트 배지 (카메라 매핑과 톤 통일) */
  filteredCount: number;
}

export function CameraFilterPopover({ filter, availability, floorPlans, selectedFloorPlanIds, floorPlanIdsWithCameras, onApply, filteredCount }: CameraFilterPopoverProps) {
  const { t } = useTranslation("analysis-draft");
  const [open, setOpen] = useState(false);
  const [floorPlanMenuOpen, setFloorPlanMenuOpen] = useState(false);
  const [local, setLocal] = useState<CameraFilterState>(filter);
  const [localFloorPlanIds, setLocalFloorPlanIds] = useState<Set<number>>(selectedFloorPlanIds);
  // disabled(대상 없는) 도면은 제외하고 선택 가능한 도면만
  const selectableFloorPlans = floorPlans.filter((fp) => floorPlanIdsWithCameras.has(fp.id));
  const selectedSelectableCount = selectableFloorPlans.filter((fp) => localFloorPlanIds.has(fp.id)).length;
  const floorPlanSummary =
    selectedSelectableCount === 0
      ? t("floorPlan.select")
      : selectedSelectableCount === selectableFloorPlans.length
        ? t("floorPlan.allSelected")
        : t("floorPlan.selected", { count: selectedSelectableCount });

  // 트리거 활성 요약 (카메라 매핑 트리거와 톤 통일) — 적용된 filter/selectedFloorPlanIds 기준
  const appliedSelectableSelected = selectableFloorPlans.filter((fp) => selectedFloorPlanIds.has(fp.id)).length;
  const triggerSummary: string[] = [];
  if (filter.analyzed !== filter.notAnalyzed) {
    triggerSummary.push(filter.analyzed ? t("filter.analyzed") : t("filter.notAnalyzed"));
  }
  if (appliedSelectableSelected < selectableFloorPlans.length) {
    triggerSummary.push(t("filter.floorPlanLabel"));
  }
  const allEventOn =
    filter.lineDefault && filter.lineTraffic && filter.lineVisit
    && filter.zoneDefault && filter.zonePurchase && filter.noEventSource;
  if (!allEventOn) triggerSummary.push(t("filter.summaryEventSource"));
  const isFiltering = triggerSummary.length > 0;
  const triggerSummaryText = isFiltering ? triggerSummary.join(" · ") : t("filter.summaryAll");

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setLocal(filter);
      setLocalFloorPlanIds(new Set(selectedFloorPlanIds));
    }
    setOpen(nextOpen);
  };

  const set = useCallback(
    <K extends keyof CameraFilterState>(key: K, value: CameraFilterState[K]) => {
      setLocal((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const toggleFloorPlan = useCallback((id: number) => {
    setLocalFloorPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleReset = () => {
    setLocal(DEFAULT_CAMERA_FILTER);
    setLocalFloorPlanIds(new Set(floorPlans.map((fp) => fp.id)));
  };

  const handleCancel = () => {
    setLocal(filter);
    setLocalFloorPlanIds(new Set(selectedFloorPlanIds));
    setOpen(false);
  };

  const handleApply = () => {
    onApply(local, localFloorPlanIds);
    setOpen(false);
  };

  return (
    <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <TriggerButton type="button" $active={isFiltering}>
          <Icon icon="ic_filter" width={14} height={14} />
          <TriggerLabel>{triggerSummaryText}</TriggerLabel>
          <TriggerCount $active={isFiltering}>{filteredCount}</TriggerCount>
        </TriggerButton>
      </RadixPopover.Trigger>
      <RadixPopover.Portal>
        <StyledContent align="end" sideOffset={4}>
          {/* Header */}
          <Header>
            <HeaderTitle>{t("filter.title")}</HeaderTitle>
            <Button variant="text" color="primary" size="xSmall" onClick={handleReset}>
              {t("filter.reset")}
            </Button>
          </Header>

          {/* (F17) 도면 필터 — 드롭다운(select). 도면이 카메라를 포함하는 상위 개념이라 맨 위. 대상 없는 도면은 disabled */}
          <Section>
            <SectionTitle>{t("filter.floorPlanLabel")}</SectionTitle>
            <FloorPlanSelect>
              <FloorPlanTrigger type="button" onClick={() => setFloorPlanMenuOpen((v) => !v)}>
                <span>{floorPlanSummary}</span>
                <Icon icon={floorPlanMenuOpen ? "ic_chevron_up" : "ic_chevron_down"} width={16} />
              </FloorPlanTrigger>
              {floorPlanMenuOpen && (
                <FloorPlanMenu>
                  <FloorPlanMenuActions>
                    <Button variant="text" color="primary" size="xSmall" onClick={() => setLocalFloorPlanIds(new Set())}>
                      {t("floorPlan.deselectAll")}
                    </Button>
                    <Button variant="text" color="primary" size="xSmall" onClick={() => setLocalFloorPlanIds(new Set(selectableFloorPlans.map((fp) => fp.id)))}>
                      {t("floorPlan.selectAll")}
                    </Button>
                  </FloorPlanMenuActions>
                  <FloorPlanMenuList>
                    {floorPlans.map((fp) => {
                      const disabled = !floorPlanIdsWithCameras.has(fp.id);
                      return (
                        <Checkbox
                          key={fp.id}
                          size="small"
                          color="primary"
                          label={fp.name}
                          checked={!disabled && localFloorPlanIds.has(fp.id)}
                          disabled={disabled}
                          onCheckedChange={(v) => typeof v === "boolean" && !disabled && toggleFloorPlan(fp.id)}
                        />
                      );
                    })}
                  </FloorPlanMenuList>
                </FloorPlanMenu>
              )}
            </FloorPlanSelect>
          </Section>

          {/* 분석 여부 */}
          <Section>
            <SectionTitle>{t("filter.analysisStatus")}</SectionTitle>
            <CheckboxRow>
              <Checkbox
                size="small"
                color="primary"
                label={t("filter.analyzed")}
                checked={availability.hasAnalyzed && local.analyzed}
                onCheckedChange={(v) => typeof v === "boolean" && set("analyzed", v)}
                disabled={!availability.hasAnalyzed}
              />
              <Checkbox
                size="small"
                color="primary"
                label={t("filter.notAnalyzed")}
                checked={availability.hasNotAnalyzed && local.notAnalyzed}
                onCheckedChange={(v) => typeof v === "boolean" && set("notAnalyzed", v)}
                disabled={!availability.hasNotAnalyzed}
              />
            </CheckboxRow>
          </Section>

          {/* 스냅샷 이벤트 소스 */}
          <Section>
            <SectionTitle>{t("filter.eventSource")}</SectionTitle>
            <SubGroups>
              {/* 라인 */}
              <SubGroup>
                <SubGroupTitle>{t("filter.line")}</SubGroupTitle>
                <CheckboxRow>
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.lineDefault")}
                    checked={availability.hasLineDefault && local.lineDefault}
                    onCheckedChange={(v) => typeof v === "boolean" && set("lineDefault", v)}
                    disabled={!availability.hasLineDefault}
                  />
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.lineTraffic")}
                    checked={availability.hasLineTraffic && local.lineTraffic}
                    onCheckedChange={(v) => typeof v === "boolean" && set("lineTraffic", v)}
                    disabled={!availability.hasLineTraffic}
                  />
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.lineVisit")}
                    checked={availability.hasLineVisit && local.lineVisit}
                    onCheckedChange={(v) => typeof v === "boolean" && set("lineVisit", v)}
                    disabled={!availability.hasLineVisit}
                  />
                </CheckboxRow>
              </SubGroup>
              {/* 구역 */}
              <SubGroup>
                <SubGroupTitle>{t("filter.zone")}</SubGroupTitle>
                <CheckboxRow>
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.zoneDefault")}
                    checked={availability.hasZoneDefault && local.zoneDefault}
                    onCheckedChange={(v) => typeof v === "boolean" && set("zoneDefault", v)}
                    disabled={!availability.hasZoneDefault}
                  />
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.zonePurchase")}
                    checked={availability.hasZonePurchase && local.zonePurchase}
                    onCheckedChange={(v) => typeof v === "boolean" && set("zonePurchase", v)}
                    disabled={!availability.hasZonePurchase}
                  />
                </CheckboxRow>
              </SubGroup>
              {/* 기타 */}
              <SubGroup>
                <SubGroupTitle>{t("filter.etc")}</SubGroupTitle>
                <CheckboxRow>
                  <Checkbox
                    size="small"
                    color="primary"
                    label={t("filter.noEventSource")}
                    checked={availability.hasNoEventSource && local.noEventSource}
                    onCheckedChange={(v) => typeof v === "boolean" && set("noEventSource", v)}
                    disabled={!availability.hasNoEventSource}
                  />
                </CheckboxRow>
              </SubGroup>
            </SubGroups>
          </Section>

          {/* Footer */}
          <Footer>
            <Button variant="outlined" color="primary" size="xSmall" onClick={handleCancel}>
              {t("filter.cancel")}
            </Button>
            <ContainedButton variant="contained" color="primary" size="xSmall" onClick={handleApply}>
              {t("filter.apply")}
            </ContainedButton>
          </Footer>
        </StyledContent>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

// --- Styles ---

const TriggerButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? "#484BD2" : "rgba(143, 149, 178, 0.32)")};
  background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.08)" : "#fff")};
  color: ${({ $active }) => ($active ? "#484BD2" : token("colors.text.primary"))};
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: ${({ $active }) => ($active ? "rgba(72, 75, 210, 0.14)" : token("colors.action.hover"))};
  }
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TriggerCount = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? "#484BD2" : "rgba(143, 149, 178, 0.24)")};
  color: ${({ $active }) => ($active ? "#fff" : token("colors.text.secondary"))};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
`;

const StyledContent = styled(RadixPopover.Content)`
  width: 320px;
  background: #fff;
  border-radius: 12px;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
  z-index: 50;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 0;
`;

const HeaderTitle = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #11111f;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 20px 0;
`;

const SectionTitle = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const CheckboxRow = styled.div`
  display: flex;
  gap: 8px;
`;

const FloorPlanSelect = styled.div`
  position: relative;
`;

const FloorPlanTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  height: 32px;
  padding: 0 8px 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(143, 149, 178, 0.18);
  background: #fff;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  cursor: pointer;
  box-sizing: border-box;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    border-color: ${token("colors.brand.primary")};
  }
`;

const FloorPlanMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  padding: 8px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #E6E8F0;
  box-shadow:
    -20px 20px 40px -4px rgba(105, 111, 140, 0.24),
    0px 0px 2px 0px rgba(105, 111, 140, 0.16);
`;

const FloorPlanMenuActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-bottom: 6px;
`;

const FloorPlanMenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
`;


const SubGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SubGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SubGroupTitle = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: #696f8c;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

// 뿌리 원인: DS contained 는 border:none, outlined 는 border:1px (box-sizing 비-border-box)
// → outlined 만 ~2px 더 커짐. contained 에 1px 투명 테두리를 예약해 크기 일치.
const ContainedButton = styled(Button)`
  && {
    border: 1px solid transparent;
  }
`;
